import { ActionsDictionary, BuildResult, Translations } from '@dynamic/ui';
import { Component } from 'react';

import { LayoutData } from '#/client/models';
import { SPINNER_SIZE, StyledSpinner } from '#/components/Splash/Splash.styled';
import {
  buildDynamicUi,
  BuildDynamicUiParams,
} from '#/dynamic/components/defaultComponents';

export interface PageRendererProps {
  buildDynamicUiParams: BuildDynamicUiParams;
  layout: LayoutData | undefined;
  actions: ActionsDictionary;
  context: any;
  cacheKey: string;
}

interface CachedBuilder extends ReturnType<typeof buildDynamicUi> {
  cacheKey: string;
}

let cachedBuilder: CachedBuilder | undefined;

const generateBuild = (
  cacheKey: string,
  buildDynamicUiParams: BuildDynamicUiParams
) => {
  if (cacheKey !== cachedBuilder?.cacheKey) {
    // invalidate the cache
    cachedBuilder = undefined;
  }

  if (!buildDynamicUiParams.v2Templates || cachedBuilder) {
    return cachedBuilder;
  }

  cachedBuilder = {
    cacheKey,
    ...buildDynamicUi(buildDynamicUiParams),
  };

  return cachedBuilder;
};

const isValidTranslations = (translations: Translations | undefined) =>
  translations &&
  typeof translations === 'object' &&
  Object.keys(translations).length > 0;

// Directly calling `buildDynamicUi` from the `PageController`component led to a React warning about changed hook order.
// The reason is that during loading, `PageController` early returned with a spinner, but after loading, it continued to
// build the Dynamic UI components, leading to more hooks called on that side.
//
// The solution was to move `buildDynamicUi` to a separate component, so its hooks don't count in the `PageController`,
// but this led to another issue: the `buildDynamicUi` makes the Dynamic UI factories create new styled components,
// which is not allowed in render methods or functional components.
//
// This is why this must be a class component, so we can do it in the constructor.
/**
 * Builds a Dynamic UI Page component and uses it to render a page.
 * @param props Props object.
 * @param props.buildDynamicUiParams The parameters needed for the `buildDynamicUi` function.
 * @param props.layout The layout object for the page, from `usePageController`.
 * @param props.actions The actions object for the page, from `useActions`.
 * @param props.context The context object for the page, from `getPageContext`.
 * @param props.cacheKey The key to cache the current dynamic-ui build, if a complete rebuild should take place, change the value of the cacheKey.
 */
export class PageRenderer extends Component<PageRendererProps> {
  // the DynUi-side type def for the Page component is missing the actions prop, but it's still better than `any`
  private Page: BuildResult['Page'] | undefined;
  private cacheKey: string | undefined;

  constructor(props: PageRendererProps) {
    super(props);
    this.build(this.props.cacheKey, this.props.buildDynamicUiParams);
  }

  shouldComponentUpdate(nextProps: Readonly<PageRendererProps>): boolean {
    // if any of the build params changed, we need to rebuild the Page component
    if (this.props.buildDynamicUiParams !== nextProps.buildDynamicUiParams) {
      this.build(nextProps.cacheKey, nextProps.buildDynamicUiParams);
    }

    return true;
  }

  build(cacheKey: string, buildDynamicUiParams: BuildDynamicUiParams) {
    if (!isValidTranslations(buildDynamicUiParams.translations)) {
      return;
    }

    this.Page = generateBuild(cacheKey, buildDynamicUiParams)?.Page;
    this.cacheKey = cacheKey;
  }

  render() {
    const { layout, actions, context, buildDynamicUiParams } = this.props;
    const { translations } = buildDynamicUiParams;
    const { Page, cacheKey } = this;

    if (!isValidTranslations(translations)) {
      return (
        <StyledSpinner
          data-testid="page-renderer-spinner"
          height={SPINNER_SIZE}
          width={SPINNER_SIZE}
        />
      );
    }

    return (
      Page &&
      layout && (
        <Page key={cacheKey} {...layout} actions={actions} context={context} />
      )
    );
  }
}
